import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, CheckCircle, Clock, Send, Gavel } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ArbitrationNoticePage = () => {
  const scrollToIntake = () => {
    window.location.assign('/#intake');
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Legal Escalation
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              Arbitration Notice
            </h1>
            <p className="text-lg md:text-xl text-[#888680] leading-relaxed">
              When standard dispute resolution fails, arbitration provides a formal legal pathway to challenge platform enforcement decisions.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* What is Arbitration */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Gavel className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">What is Platform Arbitration?</h2>
                </div>
                <div className="space-y-4 text-[#888680]">
                  <p>
                    Most major social media platforms include mandatory arbitration clauses in their Terms of Service. This means disputes must typically be resolved through arbitration rather than traditional court litigation.
                  </p>
                  <p>
                    Arbitration is a form of alternative dispute resolution where an independent arbitrator (or panel) reviews the case and makes a binding decision. For platform disputes, this process is governed by organizations like the American Arbitration Association (AAA) or JAMS.
                  </p>
                </div>
              </div>

              {/* When Arbitration Applies */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <AlertTriangle className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">When Arbitration May Be Appropriate</h2>
                </div>
                <div className="space-y-4 text-[#888680]">
                  <p>Arbitration is typically considered when:</p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Standard appeals and escalation have been exhausted</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Notice of Dispute has been sent without adequate response</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>Significant financial damages can be documented</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>There&apos;s a clear procedural or policy violation by the platform</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#d9fb06] mt-1 flex-shrink-0" />
                      <span>The account has substantial business value</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* The Process */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">Arbitration Process Overview</h2>
                </div>
                <div className="space-y-6">
                  {[
                    { step: '1', title: 'Pre-Arbitration Notice', desc: 'Formal notice sent to platform stating intent to pursue arbitration if dispute is not resolved.' },
                    { step: '2', title: 'Filing the Demand', desc: 'If no resolution, a formal Demand for Arbitration is filed with AAA or JAMS.' },
                    { step: '3', title: 'Arbitrator Selection', desc: 'Both parties participate in selecting a neutral arbitrator.' },
                    { step: '4', title: 'Discovery & Briefing', desc: 'Exchange of evidence and submission of legal briefs.' },
                    { step: '5', title: 'Hearing', desc: 'Virtual or in-person hearing where both sides present their case.' },
                    { step: '6', title: 'Award', desc: 'Arbitrator issues a binding decision.' }
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#d9fb06] flex items-center justify-center flex-shrink-0">
                        <span className="text-[#1a1c1b] font-bold text-sm">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="text-[#dfddd6] font-medium">{item.title}</h4>
                        <p className="text-[#888680] text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Important Considerations */}
              <div className="bg-[#252825] border border-[#d9fb06]/30 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Scale className="w-6 h-6 text-[#d9fb06]" />
                  <h2 className="text-xl font-bold text-[#d9fb06]">Important Considerations</h2>
                </div>
                <div className="space-y-4 text-[#888680]">
                  <p>
                    <strong className="text-[#dfddd6]">Cost:</strong> Arbitration involves filing fees and potentially attorney fees. However, many platform Terms of Service require the platform to pay arbitration fees for consumer disputes below certain thresholds.
                  </p>
                  <p>
                    <strong className="text-[#dfddd6]">Time:</strong> Arbitration typically takes 3-6 months from filing to award, though this varies.
                  </p>
                  <p>
                    <strong className="text-[#dfddd6]">Settlement:</strong> Many cases settle before arbitration hearing. The filing of arbitration often prompts platforms to engage more seriously in resolution discussions.
                  </p>
                  <p>
                    <strong className="text-[#dfddd6]">Binding Decision:</strong> Arbitration awards are generally final and binding with very limited grounds for appeal.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Our Role */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-[#d9fb06] mb-4">How We Help</h3>
                <div className="space-y-4 text-[#888680] text-sm">
                  <p>
                    Social Dispute Co. prepares arbitration-ready documentation packages, including:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Send className="w-4 h-4 text-[#d9fb06] mt-0.5 flex-shrink-0" />
                      <span>Pre-arbitration demand letters</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Send className="w-4 h-4 text-[#d9fb06] mt-0.5 flex-shrink-0" />
                      <span>Evidence compilation and organization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Send className="w-4 h-4 text-[#d9fb06] mt-0.5 flex-shrink-0" />
                      <span>Damages documentation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Send className="w-4 h-4 text-[#d9fb06] mt-0.5 flex-shrink-0" />
                      <span>Policy violation analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Send className="w-4 h-4 text-[#d9fb06] mt-0.5 flex-shrink-0" />
                      <span>Attorney coordination</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-[#252825] border border-[#3f4816] rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-[#d9fb06]" />
                  <h3 className="text-lg font-bold text-[#d9fb06]">Typical Timeline</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-[#888680]">
                    <span>Documentation prep</span>
                    <span className="text-[#d9fb06]">1-2 weeks</span>
                  </div>
                  <div className="flex justify-between text-[#888680]">
                    <span>Pre-arb notice period</span>
                    <span className="text-[#d9fb06]">30-60 days</span>
                  </div>
                  <div className="flex justify-between text-[#888680]">
                    <span>Arbitration process</span>
                    <span className="text-[#d9fb06]">3-6 months</span>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-[#d9fb06] rounded-2xl p-6 text-center">
                <h3 className="text-lg font-bold text-[#1a1c1b] mb-2">Need Arbitration Support?</h3>
                <p className="text-[#1a1c1b]/80 text-sm mb-4">
                  We&apos;ll evaluate your case and advise whether arbitration is appropriate.
                </p>
                <Button 
                  onClick={scrollToIntake}
                  className="bg-[#1a1c1b] text-[#d9fb06] hover:bg-[#1a1c1b]/90 rounded-full w-full font-semibold"
                  data-testid="arbitration-cta"
                >
                  Request Evaluation
                </Button>
              </div>

              {/* Disclaimer */}
              <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-xl p-4">
                <p className="text-[#888680] text-xs">
                  This page is for informational purposes only and does not constitute legal advice. Consult a licensed attorney for advice specific to your situation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ArbitrationNoticePage;
